import type { RouterHistory } from '@tanstack/react-router'

import type { SessionSource } from '@/lib/auth/session-source'
import { setSessionSource, useAuthStore } from '@/lib/auth/store'
import { listen, postSignedOut, requestToken } from '@/lib/embed/bridge'
import { keepOnChart } from '@/lib/embed/navigation'
import { setAppTheme } from '@/lib/ui-store'

/**
 * The Flutter app as the session's source. It holds the refresh token, so
 * nothing here touches mm-be's cookie: a cookie refresh from the WebView would
 * mint a second session beside the app's, and a cookie logout would end
 * neither. The access token lives in memory only — the app answers a reload's
 * `boot` ask in a moment, so a copy in storage would buy nothing but a bearer
 * token at rest.
 */
const appSource: SessionSource = {
  restore: () => null,
  keep: () => {},
  forget: () => {},
  renew: requestToken,
  // Nothing to end here: the app owns the session, and is told it ended by
  // the watcher in `startEmbed`.
  endSession: () => Promise.resolve(),
}

/**
 * Embed mode's boot, called by main.tsx inside the app's WebView only. It
 * must run before the first `ensureSession`: that is the first `token` ask,
 * and both the answer's listener and the source it goes through have to be
 * in place by then.
 *
 * Every way the session ends here — the app said signed-out (at boot or
 * since), the app could not answer at boot, or mm-be refused a token the app
 * had just minted — lands in the store as `anonymous`, and this one watcher
 * tells the app. The page shows the ended state for that same store status
 * (routes/layouts/workspace.tsx); nothing sends the member to /signin.
 */
export const startEmbed = ({
  history,
  staysOnChart,
}: {
  history: RouterHistory
  staysOnChart: (pathname: string) => boolean
}): void => {
  setSessionSource(appSource)

  listen({
    onToken: (access) => {
      useAuthStore.getState().swapToken(access)
    },
    onSignedOut: () => {
      if (useAuthStore.getState().status !== 'anonymous') useAuthStore.getState().signOut('member')
    },
    onTheme: setAppTheme,
  })

  useAuthStore.subscribe((state, previous) => {
    if (state.status !== 'anonymous' || previous.status === 'anonymous') return
    postSignedOut(state.signedOutBy === 'rejected' ? 'rejected' : 'expired')
  })

  keepOnChart({ history, staysOnChart })
}
